<template>
  <div>
    <div v-if="shouldShowRegistration && !isWorkshopFull" class="w-full px-4" data-testid="workshop-registration-form">
      <!-- Seats availability info -->
      <div v-if="seatsAvailability && seatsAvailability.capacity" class="mb-5 p-4 bg-ivory-dark rounded-sm">
        <div class="text-sm font-bold">
          <span v-if="seatsAvailability.seats_remaining && seatsAvailability.seats_remaining <= 5" class="text-tangerine">
            ⚠️ Only {{ seatsAvailability.seats_remaining }} seats remaining!
          </span>
          <span v-else-if="seatsAvailability.seats_remaining" class="text-gray-700">
            {{ seatsAvailability.seats_remaining }} seats available
          </span>
        </div>
      </div>

      <div>
        <div class="mb-3  text-xl font-extrabold">
          Please fill in the attendee info:
        </div>
        <label for="attendeeFirstName" class="block">First name</label>
        <input
          v-model="attendeeFirstName"
          type="text"
          name="attendeeFirstName"
          class="mt-1 h-10 w-full bg-white px-5 text-lg shadow-block-tangerine outline-hidden transition-all"
        >
        <label for="attendeeLastName" class="mt-4 block">Last name</label>
        <input
          v-model="attendeeLastName"
          type="text"
          name="attendeeLastName"
          class="mt-1 h-10 w-full bg-white px-5 text-lg shadow-block-tangerine outline-hidden transition-all"
        >
        <label for="attendeeEmail" class="mt-4 block">Email</label>
        <input
          v-model="attendeeEmail"
          class="mt-1 h-10 w-full bg-white px-5 text-lg shadow-block-tangerine outline-hidden transition-all"
        >
      </div>
      <div>
        <p
          class="mt-5 text-sm text-punch"
          :class="errors.length ? 'opacity-100' : 'opacity-0'"
        >
          {{ errorMessage }}
        </p>
      </div>
      <div id="registration" class="mt-1">
        <button class="mt-5 w-full bg-white px-7 py-3 text-xl font-black uppercase shadow-block-lime transition-all hover:shadow-block-hover-lime" @click="pay(); useTrackEvent('PayButton - ' + info?.title)">
          Sign up for workshop
        </button>
        <span class="mt-5 block text-center text-sm text-gray-500">After clicking "Sign up" button, will be redirected to <IconStripe class="inline-block h-5 pb-0.5" /> checkout page</span>
      </div>
    </div>
    <div v-else-if="shouldShowRegistration && isWorkshopFull" class="grid h-full place-items-center bg-ivory-dark p-7 text-xl font-semibold">
      <div>
        <div class="text-center text-punch font-bold mb-3">Workshop is Fully Booked</div>
        <div class="text-center text-base">All seats for this workshop have been filled. Please check back for future dates.</div>
      </div>
    </div>
    <div v-else class="grid h-full place-items-center bg-ivory-dark p-7 text-xl font-semibold">
      <div>Registration to the workshop is currently closed. A new date will be released soon.</div>
    </div>
  </div>
</template>
<script setup lang="ts">

import { loadStripe } from '@stripe/stripe-js'
import { type Ref } from 'vue'
import { isUpcomingWorkshop } from '~/helpers/isUpcomingWorkshop'
import type { WorkshopSeatsAvailability } from '~/types/supabase'

const config = useRuntimeConfig()
const stripe = await loadStripe(config.public.stripeApiKey)
const { getSeatsAvailability } = useSupabaseWorkshops()

const attendeeFirstName = ref()
const attendeeLastName = ref()
const attendeeEmail = ref()
const errorMessage = ref()

const errors: Ref<string[]> = ref([])
const errorAttendeeFirstName = ref(false)
const errorAttendeeLastName = ref(false)
const errorAttendeeEmail = ref(false)
const shouldShowRegistration = computed(() => isUpcomingWorkshop(props.info?.workshop_dates ?? null))
const seatsAvailability = ref<WorkshopSeatsAvailability | null>(null)
const isWorkshopFull = ref(false)

const props = defineProps({
  info: {
    type: Object,
    default: undefined
  },
  price: {
    type: Object,
    default: () => {
      return {
        data: {
          id: String
        }
      }
    }
  }
})

onMounted(async () => {
  if (shouldShowRegistration.value) {
    // Fetch seat availability if workshop has a date
    if (props.info.workshop_dates.id) {
      const { availability, error } = await getSeatsAvailability(props.info.workshop_dates.id)
      if (!error && availability) {
        seatsAvailability.value = availability
        isWorkshopFull.value = availability.is_full
      }
    }
  }
})

function requiredFields (errors: string[]) {
  let message = ''
  switch (errors.length) {
    case 1:
      message = `${errors[0]} is required`
      break
    case 2:
      message = `${errors[0]} and ${errors[1]} are required`
      break
    default:
      // eslint-disable-next-line no-case-declarations
      const lastItem = errors.pop()
      message = `${errors.join(', ')} and ${lastItem} are required`
      break
  }
  return message.charAt(0).toUpperCase() + message.slice(1)
}

const pay = () => {
  if (!shouldShowRegistration.value || isWorkshopFull.value) return
  if (attendeeFirstName.value && attendeeLastName.value && attendeeEmail.value) {
    useFetch('/api/checkout', {
      method: 'POST',
      body: {
        order: {
          quantity: 1,
          price: props.price?.data.id
        },
        metadata: {
          workshop_date_id: props.info?.workshop_dates?.id,
          date: props.info?.workshop_dates?.date_display,
          item: props.info?.title,
          type: props.info?.type,
          attendeeFirstName: attendeeFirstName.value,
          attendeeLastName: attendeeLastName.value,
          attendeeEmail: attendeeEmail.value,
          redirectPath: '/workshop/registration'
        }
      }
    }).then(({ data }) => {
    // @ts-ignore
      stripe?.redirectToCheckout({ sessionId: data?.value?.id })
    })
  } else {
    errors.value = []

    if (!attendeeFirstName.value) {
      errorAttendeeFirstName.value = true
      errors.value.push('first name')
    }
    if (!attendeeLastName.value) {
      errorAttendeeLastName.value = true
      errors.value.push('last name')
    }
    if (!attendeeEmail.value) {
      errorAttendeeEmail.value = true
      errors.value.push('email')
    }

    errorMessage.value = requiredFields(errors.value)
  }
}

</script>
